import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  FileText, Upload, Download, Trash2, Plus, User, Calendar,
  GraduationCap, FileCheck, Award, Search, Filter, FolderOpen, 
  Wand2, FileOutput, Building2, Users, RefreshCcw, Eye, FileSignature,
  CheckCircle, Clock, Archive
} from 'lucide-react';
import { toast } from 'sonner';

// Document type config
const DOCUMENT_TYPES = {
  administratif: { label: "Documents administratifs", icon: FileText, color: "bg-blue-100 text-blue-700" },
  pedagogique: { label: "Documents pédagogiques", icon: GraduationCap, color: "bg-emerald-100 text-emerald-700" },
  certificat: { label: "Certificats / Attestations", icon: Award, color: "bg-violet-100 text-violet-700" },
  a_signer: { label: "Documents à signer", icon: FileSignature, color: "bg-amber-100 text-amber-700" },
};

const TARGET_TYPES = {
  student: { label: "Étudiant", icon: User },
  trainer: { label: "Formateur", icon: GraduationCap },
  group: { label: "Groupe", icon: Users },
  center: { label: "Centre", icon: Building2 },
};

const STATUS_CONFIG = {
  actif: { label: "Actif", icon: CheckCircle, color: "bg-emerald-100 text-emerald-700" },
  en_attente_signature: { label: "En attente", icon: Clock, color: "bg-amber-100 text-amber-700" },
  signe: { label: "Signé", icon: FileSignature, color: "bg-blue-100 text-blue-700" },
  archive: { label: "Archivé", icon: Archive, color: "bg-slate-100 text-slate-600" },
};

const CfaAdminDocumentsPage = ({ onMenuClick }) => {
  const [documents, setDocuments] = useState([]);
  const [students, setStudents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('all');
  const [filterStudent, setFilterStudent] = useState('all');
  const [filterTrainer, setFilterTrainer] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Upload form
  const [uploadForm, setUploadForm] = useState({
    title: '',
    document_type: 'administratif',
    target_type: 'student',
    category: '',
    description: '',
    student_id: '',
    trainer_id: '',
    group_id: '',
    notify_target: true,
    file: null
  });

  // Generate form
  const [generateForm, setGenerateForm] = useState({
    template_type: 'certificat_scolarite',
    student_id: '',
    trainer_id: '',
    course_id: '',
    notify_target: true,
    save_document: true
  });

  // PDF Templates
  const PDF_TEMPLATES = [
    { id: 'certificat_scolarite', title: 'Certificat de scolarité', description: "Atteste de l'inscription de l'étudiant au sein de l'établissement" },
    { id: 'attestation_formation', title: 'Attestation de formation', description: 'Atteste de la participation et du suivi complet de la formation' },
    { id: 'certificat_reussite', title: 'Certificat de réussite', description: 'Certifie la validation et la réussite à une formation' },
    { id: 'attestation_competences', title: 'Attestation de compétences', description: 'Atteste des compétences professionnelles acquises' },
    { id: 'convention_formation', title: 'Convention de formation', description: 'Convention tripartite pré-remplie avec les données étudiant et entreprise (si rattachée)' }
  ];

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, studentsRes, trainersRes, groupsRes, coursesRes, statsRes] = await Promise.all([
        api.get('/documents', { params: {
          document_type: activeTab !== 'all' ? activeTab : undefined,
          target_type: filterTargetType !== 'all' ? filterTargetType : undefined,
          student_id: filterStudent !== 'all' ? filterStudent : undefined,
          trainer_id: filterTrainer !== 'all' ? filterTrainer : undefined,
          group_id: filterGroup !== 'all' ? filterGroup : undefined,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          search: searchQuery || undefined
        }}),
        api.get('/documents/students').catch(() => ({ data: [] })),
        api.get('/documents/trainers').catch(() => ({ data: [] })),
        api.get('/documents/groups').catch(() => ({ data: [] })),
        api.get('/courses').catch(() => ({ data: [] })),
        api.get('/documents/stats').catch(() => ({ data: {} }))
      ]);
      
      setDocuments(docsRes.data);
      setStudents(studentsRes.data);
      setTrainers(trainersRes.data);
      setGroups(groupsRes.data);
      setCourses(coursesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterTargetType, filterStudent, filterTrainer, filterGroup, filterStatus, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch categories when upload form changes
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/documents/categories', {
          params: {
            document_type: uploadForm.document_type,
            target_type: uploadForm.target_type
          }
        });
        setCategories(res.data);
      } catch (error) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, [uploadForm.document_type, uploadForm.target_type]);

  // Handle file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    // Validate target
    if (uploadForm.target_type === 'student' && !uploadForm.student_id) {
      toast.error('Veuillez sélectionner un étudiant');
      return;
    }
    if (uploadForm.target_type === 'trainer' && !uploadForm.trainer_id) {
      toast.error('Veuillez sélectionner un formateur');
      return;
    }
    if (uploadForm.target_type === 'group' && !uploadForm.group_id) {
      toast.error('Veuillez sélectionner un groupe');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('document_type', uploadForm.document_type);
      formData.append('target_type', uploadForm.target_type);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      formData.append('notify_target', uploadForm.notify_target);
      formData.append('file', uploadForm.file);
      
      if (uploadForm.target_type === 'student') {
        formData.append('student_id', uploadForm.student_id);
      } else if (uploadForm.target_type === 'trainer') {
        formData.append('trainer_id', uploadForm.trainer_id);
      } else if (uploadForm.target_type === 'group') {
        formData.append('group_id', uploadForm.group_id);
      }

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document téléchargé avec succès');
      setShowUploadDialog(false);
      setUploadForm({
        title: '', document_type: 'administratif', target_type: 'student',
        category: '', description: '', student_id: '', trainer_id: '', group_id: '',
        notify_target: true, file: null
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  // Handle PDF generation
  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!generateForm.student_id && !generateForm.trainer_id) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }

    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('template_type', generateForm.template_type);
      formData.append('save_document', generateForm.save_document);
      formData.append('notify_target', generateForm.notify_target);
      
      if (generateForm.student_id) {
        formData.append('student_id', generateForm.student_id);
      }
      if (generateForm.trainer_id) {
        formData.append('trainer_id', generateForm.trainer_id);
      }
      if (generateForm.course_id) {
        formData.append('course_id', generateForm.course_id);
      }

      const response = await api.post('/documents/generate-pdf', formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      
      // Verify we got a PDF
      if (response.data && response.data.size > 0) {
        // Download the PDF
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Extract filename from response headers if available
        const contentDisposition = response.headers?.['content-disposition'];
        let filename = `document_${Date.now()}.pdf`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match && match[1]) {
            filename = match[1].replace(/"/g, '');
          }
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Document généré et téléchargé avec succès');
        setShowGenerateDialog(false);
        
        // Reset form
        setGenerateForm({
          template_type: 'certificat_scolarite',
          student_id: '',
          trainer_id: '',
          course_id: '',
          notify_target: true,
          save_document: true
        });
        
        // Refresh document list if saved
        if (generateForm.save_document) {
          setTimeout(() => fetchData(), 500);
        }
      } else {
        throw new Error('Document généré vide');
      }
    } catch (error) {
      console.error('Error generating:', error);
      if (error.response?.status === 404) {
        toast.error('Destinataire non trouvé');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Délai dépassé. Veuillez réessayer.');
      } else {
        toast.error('Erreur lors de la génération du document');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedDocument) return;
    
    try {
      await api.delete(`/documents/${selectedDocument.id}`);
      toast.success('Document supprimé');
      setShowDeleteDialog(false);
      setSelectedDocument(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Download document via authenticated endpoint (never open public /uploads URL)
  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || doc.title || `document_${doc.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error(
        error.response?.status === 403
          ? 'Accès non autorisé à ce document'
          : 'Erreur lors du téléchargement'
      );
    }
  };

  // Stats by type
  const statsByType = stats.by_type || {};

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Gestion des Documents" onMenuClick={onMenuClick} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
                  <p className="text-sm text-slate-600">Total</p>
                </div>
                <FolderOpen className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-700">{statsByType.administratif || 0}</p>
                  <p className="text-sm text-blue-600">Administratifs</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{statsByType.pedagogique || 0}</p>
                  <p className="text-sm text-emerald-600">Pédagogiques</p>
                </div>
                <GraduationCap className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-violet-50 border-violet-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-violet-700">{statsByType.certificat || 0}</p>
                  <p className="text-sm text-violet-600">Certificats</p>
                </div>
                <Award className="w-8 h-8 text-violet-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-amber-700">{statsByType.a_signer || 0}</p>
                  <p className="text-sm text-amber-600">À signer</p>
                </div>
                <FileSignature className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-doc-btn">
              <Upload className="w-4 h-4 mr-2" />
              Déposer un document
            </Button>
            <Button variant="outline" onClick={() => setShowGenerateDialog(true)} data-testid="generate-pdf-btn">
              <Wand2 className="w-4 h-4 mr-2" />
              Générer un PDF
            </Button>
            <Button variant="outline" onClick={fetchData} data-testid="refresh-docs-btn">
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>

          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-docs-input"
            />
          </div>
        </div>

        {/* Tabs and Filters */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <TabsList>
              <TabsTrigger value="all">Tous</TabsTrigger>
              <TabsTrigger value="administratif">Administratifs</TabsTrigger>
              <TabsTrigger value="pedagogique">Pédagogiques</TabsTrigger>
              <TabsTrigger value="certificat">Certificats</TabsTrigger>
              <TabsTrigger value="a_signer">À signer</TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={filterTargetType}
                onChange={(e) => setFilterTargetType(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                data-testid="filter-target-type"
              >
                <option value="all">Tous destinataires</option>
                <option value="student">Étudiants</option>
                <option value="trainer">Formateurs</option>
                <option value="group">Groupes</option>
                <option value="center">Centre</option>
              </select>
              
              {filterTargetType === 'student' && (
                <select
                  value={filterStudent}
                  onChange={(e) => setFilterStudent(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                  data-testid="filter-student"
                >
                  <option value="all">Tous les étudiants</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.full_name}</option>
                  ))}
                </select>
              )}
              
              {filterTargetType === 'trainer' && (
                <select
                  value={filterTrainer}
                  onChange={(e) => setFilterTrainer(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                  data-testid="filter-trainer"
                >
                  <option value="all">Tous les formateurs</option>
                  {trainers.map(t => (
                    <option key={t.id} value={t.id}>{t.full_name}</option>
                  ))}
                </select>
              )}
              
              {filterTargetType === 'group' && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                  data-testid="filter-group"
                >
                  <option value="all">Tous les groupes</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              )}
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                data-testid="filter-status"
              >
                <option value="all">Tous statuts</option>
                <option value="actif">Actif</option>
                <option value="en_attente_signature">En attente</option>
                <option value="signe">Signé</option>
                <option value="archive">Archivé</option>
              </select>
            </div>
          </div>
        </Tabs>

        {/* Documents List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-violet-600" />
              {documents.length} Document{documents.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun document trouvé</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowUploadDialog(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Déposer un document
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-3 px-2">Document</th>
                      <th className="py-3 px-2">Type</th>
                      <th className="py-3 px-2">Destinataire</th>
                      <th className="py-3 px-2">Catégorie</th>
                      <th className="py-3 px-2">Statut</th>
                      <th className="py-3 px-2">Date</th>
                      <th className="py-3 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {documents.map(doc => {
                      const typeConfig = DOCUMENT_TYPES[doc.document_type] || DOCUMENT_TYPES.administratif;
                      const TypeIcon = typeConfig.icon;
                      const targetConfig = TARGET_TYPES[doc.target_type] || TARGET_TYPES.student;
                      const TargetIcon = targetConfig.icon;
                      const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.actif;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={doc.id} className="hover:bg-slate-50" data-testid={`doc-row-${doc.id}`}>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="font-medium text-slate-900">{doc.title}</p>
                                {doc.original_filename && (
                                  <p className="text-xs text-slate-500">{doc.original_filename}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>
                              <TypeIcon className="w-3 h-3" />
                              {typeConfig.label.split(' ')[0]}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-1 text-slate-600">
                              <TargetIcon className="w-4 h-4" />
                              <span>{doc.target_name || targetConfig.label}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-slate-600">{doc.category_label || '-'}</span>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-slate-500">
                            {doc.generated_at ? new Date(doc.generated_at).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(doc)}
                                title="Télécharger"
                                data-testid={`download-doc-${doc.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedDocument(doc);
                                  setShowDeleteDialog(true);
                                }}
                                title="Supprimer"
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-doc-${doc.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-violet-600" />
                Déposer un document
              </DialogTitle>
              <DialogDescription>
                Téléchargez un document vers le dossier approprié
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titre *</label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Nom du document"
                  required
                  data-testid="upload-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select
                    value={uploadForm.document_type}
                    onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value, category: '' })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300"
                    data-testid="upload-type"
                  >
                    {Object.entries(DOCUMENT_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Destinataire</label>
                  <select
                    value={uploadForm.target_type}
                    onChange={(e) => setUploadForm({ ...uploadForm, target_type: e.target.value, student_id: '', trainer_id: '', group_id: '', category: '' })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300"
                    data-testid="upload-target-type"
                  >
                    {Object.entries(TARGET_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Target selection based on type */}
              {uploadForm.target_type === 'student' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Étudiant *</label>
                  <select
                    value={uploadForm.student_id}
                    onChange={(e) => setUploadForm({ ...uploadForm, student_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300"
                    required
                    data-testid="upload-student"
                  >
                    <option value="">Sélectionner un étudiant</option>
                    {students.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.full_name} {s.group_name ? `(${s.group_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {uploadForm.target_type === 'trainer' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Formateur *</label>
                  <select
                    value={uploadForm.trainer_id}
                    onChange={(e) => setUploadForm({ ...uploadForm, trainer_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300"
                    required
                    data-testid="upload-trainer"
                  >
                    <option value="">Sélectionner un formateur</option>
                    {trainers.map(t => (
                      <option key={t.id} value={t.id}>{t.full_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {uploadForm.target_type === 'group' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Groupe *</label>
                  <select
                    value={uploadForm.group_id}
                    onChange={(e) => setUploadForm({ ...uploadForm, group_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300"
                    required
                    data-testid="upload-group"
                  >
                    <option value="">Sélectionner un groupe</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({g.student_count} étudiants)</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300"
                  data-testid="upload-category"
                >
                  <option value="">Sélectionner</option>
                  {categories.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Description optionnelle"
                  rows={2}
                  data-testid="upload-description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fichier *</label>
                <Input
                  type="file"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                  required
                  data-testid="upload-file"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={uploadForm.notify_target}
                  onCheckedChange={(checked) => setUploadForm({ ...uploadForm, notify_target: checked })}
                />
                <label className="text-sm text-slate-600">Notifier le destinataire</label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={uploading} data-testid="upload-submit">
                  {uploading ? (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Télécharger
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Generate PDF Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-violet-600" />
                Générer un document PDF
              </DialogTitle>
              <DialogDescription>
                Créez un document officiel pré-rempli avec les données du centre
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Modèle de document</label>
                <select
                  value={generateForm.template_type}
                  onChange={(e) => setGenerateForm({ ...generateForm, template_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300"
                  data-testid="generate-template"
                >
                  {PDF_TEMPLATES.map(t => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {PDF_TEMPLATES.find(t => t.id === generateForm.template_type)?.description}
                </p>
                {generateForm.template_type === 'convention_formation' && (
                  <div className="mt-2 p-2 bg-violet-50 rounded-lg border border-violet-200">
                    <p className="text-xs text-violet-700">
                      <strong>Convention automatique :</strong> Si l'étudiant est rattaché à une entreprise d'accueil, 
                      toutes les informations (nom, SIRET, contact) seront automatiquement incluses dans la convention.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Étudiant *</label>
                <select
                  value={generateForm.student_id}
                  onChange={(e) => setGenerateForm({ ...generateForm, student_id: e.target.value, trainer_id: '' })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300"
                  data-testid="generate-student"
                >
                  <option value="">Sélectionner un étudiant</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.full_name} {s.group_name ? `(${s.group_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Formation (optionnel)</label>
                <select
                  value={generateForm.course_id}
                  onChange={(e) => setGenerateForm({ ...generateForm, course_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300"
                  data-testid="generate-course"
                >
                  <option value="">Sélectionner une formation</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={generateForm.save_document}
                    onCheckedChange={(checked) => setGenerateForm({ ...generateForm, save_document: checked })}
                  />
                  <label className="text-sm text-slate-600">Enregistrer dans le dossier étudiant</label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={generateForm.notify_target}
                    onCheckedChange={(checked) => setGenerateForm({ ...generateForm, notify_target: checked })}
                  />
                  <label className="text-sm text-slate-600">Notifier l'étudiant</label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowGenerateDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={generating} data-testid="generate-submit">
                  {generating ? (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <FileOutput className="w-4 h-4 mr-2" />
                      Générer le PDF
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Supprimer le document</DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer "{selectedDocument?.title}" ? Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CfaAdminDocumentsPage;
